import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { PFE_ICONS } from 'constants/icons';
import { PERMISSION } from 'constants/logic';
import { schedulePropTypes } from 'constants/propTypes';
import { selectActiveVenue, selectUserHasPermission } from 'selectors/general';

import {
  cancelSchedule,
  openCancelScheduleModal,
  redirectToManageClass,
  redirectToManageSchedule,
} from '../YieldManagement.saga';
import ActionsDropdown from './ActionsDropdown';
import { getActionWindows } from './ScheduleDetailV2.helpers';
import messages from './ScheduleDetailV2.messages';

const EditActionsMenu = ({ schedule }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { isMsInventoryVenue, venueId } = useSelector(selectActiveVenue);
  const shouldShowCancelRecurring = useSelector(state =>
    selectUserHasPermission(state, PERMISSION.CAN_CANCEL_ALL_FUTURE)
  );
  const cannotCancelClass = useSelector(state => selectUserHasPermission(state, PERMISSION.REMOVE_CLASS_CANCEL));

  const { canPerformScheduleActions, isInExtendedActionsWindow } = getActionWindows(schedule);
  if (!canPerformScheduleActions && !isInExtendedActionsWindow) {
    return null;
  }

  const { apiGuid, isCancellable, isDisabled, isInThePast, scheduleClass, startDate, startDateTime } = schedule;
  const canCancel = !cannotCancelClass && isCancellable && !isDisabled;
  const dispatchCancelSchedule = (...args) => dispatch(cancelSchedule(...args));

  const items = [
    {
      key: 'edit-class-details',
      label: <Message message={messages.editClassDetails} />,
      onSelect: () => dispatch(redirectToManageClass(venueId, scheduleClass?.classId)),
    },
    !isInExtendedActionsWindow &&
      !isInThePast &&
      isMsInventoryVenue &&
      !isDisabled && {
        key: 'edit-staff',
        label: <Message message={messages.editStaff} />,
        onSelect: () => dispatch(redirectToManageSchedule({ apiGuid, startDate, startDateTime, venueId })),
      },
    canCancel && {
      danger: true,
      key: 'cancel-class',
      label: <Message message={messages.cancelClass} />,
      onSelect: () =>
        dispatch(
          openCancelScheduleModal({
            activeSchedule: schedule,
            cancelSchedule: dispatchCancelSchedule,
            isMsInventoryVenue,
            isRecurring: false,
          })
        ),
    },
    canCancel &&
      !isMsInventoryVenue &&
      shouldShowCancelRecurring && {
        danger: true,
        key: 'cancel-all-future',
        label: <Message message={messages.cancelAllFuture} />,
        onSelect: () =>
          dispatch(
            openModal('CancelScheduleModal', {
              activeSchedule: schedule,
              cancelSchedule: dispatchCancelSchedule,
              isRecurring: true,
            })
          ),
      },
  ].filter(Boolean);

  return (
    <ActionsDropdown
      dataCypress="schedule-detail-v2-edit-menu"
      icon={PFE_ICONS.editPencilOutline}
      items={items}
      label={intl.formatMessage(messages.editMenuLabel)}
    />
  );
};

EditActionsMenu.propTypes = {
  schedule: PropTypes.shape(schedulePropTypes),
};

export default EditActionsMenu;
